import type {
  UxCartSnapshot,
  UxJourneyContext,
  UxJourneyEventName,
  UxJourneyMetrics,
} from '~/types/ux-journey';

type TrackJourneyOptions = {
  immediate?: boolean;
  useBeacon?: boolean;
};

function flattenGaParams(
  payload: Record<string, unknown>,
): Record<string, string | number | boolean | null> {
  const flat: Record<string, string | number | boolean | null> = {};

  const assign = (key: string, value: unknown) => {
    if (value === undefined || value === null) return;
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      flat[key] = value;
    }
  };

  for (const [key, value] of Object.entries(payload)) {
    if (key === 'context' && value && typeof value === 'object') {
      const ctx = value as UxJourneyContext;
      assign('region', ctx.region ?? null);
      assign('device_type', ctx.device_type ?? null);
      assign('funnel_step', ctx.funnel_step ?? null);
      assign('route_path', ctx.route_path ?? null);
      continue;
    }

    if (key === 'metrics' && value && typeof value === 'object') {
      const metrics = value as UxJourneyMetrics;
      assign('time_from_session_start_ms', metrics.time_from_session_start_ms);
      assign('time_from_first_cart_ms', metrics.time_from_first_cart_ms);
      assign('time_in_checkout_ms', metrics.time_in_checkout_ms);
      continue;
    }

    if (key === 'cart_snapshot' && value && typeof value === 'object') {
      const cart = value as UxCartSnapshot;
      assign('cart_id', cart.cart_id);
      assign('cart_item_count', cart.item_count);
      assign('cart_total', cart.total);
      assign('cart_has_items', cart.has_items);
      assign('cart_type', cart.cart_type ?? null);
      assign('cart_category', cart.category ?? null);
      assign('proposal_code', cart.proposal_code ?? null);
      assign('from_proposal', cart.from_proposal ?? null);
      continue;
    }

    if (key === 'page_sequence' && Array.isArray(value)) {
      flat.page_sequence = value.slice(-10).join(' > ');
      continue;
    }

    assign(key, value);
  }

  return flat;
}

export function useJourneyTracker() {
  const { track, getJourneyTrackingParams, getUtmParams } = useTracker();
  const { getJourneyContext } = useUserJourney();

  function pushToDataLayer(
    event: string,
    data: Record<string, string | number | boolean | null>,
  ): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      eventCategory: 'ux_journey',
      ...data,
    });
  }

  function trackJourney(
    eventName: UxJourneyEventName | string,
    payload: Record<string, unknown> = {},
    _options?: TrackJourneyOptions,
  ): void {
    if (typeof window === 'undefined') return;

    const context = getJourneyContext();
    const fullPayload: Record<string, unknown> = {
      event: eventName,
      timestamp: Date.now(),
      context,
      ...getJourneyTrackingParams(),
      ...getUtmParams(),
      ...payload,
    };

    const gaParams = flattenGaParams(fullPayload);

    track(eventName, gaParams);
    pushToDataLayer(eventName, gaParams);
  }

  return { trackJourney };
}
